import React, { useState, useEffect } from 'react';
import { Gift, X, Check, AlertTriangle, RefreshCw } from 'lucide-react';
import { ref, get } from 'firebase/database';
import { database } from '../../firebase';

const GiftCardRedemption = ({ onApply, onRemove, appliedGiftCard }) => {
  const [giftCardCode, setGiftCardCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isValid, setIsValid] = useState(false);
  
  // Function to format gift card code with dashes for display
  const formatGiftCardCode = (code) => {
    if (!code) return '';
    
    // Clean up any existing formatting
    const cleanCode = code.replace(/-/g, '').toUpperCase();
    
    // Format with dashes every 4 characters
    let formattedCode = '';
    for (let i = 0; i < cleanCode.length; i++) {
      if (i > 0 && i % 4 === 0) formattedCode += '-';
      formattedCode += cleanCode[i];
    }
    
    return formattedCode;
  };
  
  // Handle input change and format code
  const handleInputChange = (e) => {
    const input = e.target.value;
    
    // Remove any non-alphanumeric characters for storage
    const cleanCode = input.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    
    // Apply formatting for display
    const formatted = formatGiftCardCode(cleanCode);
    
    setGiftCardCode(formatted);
    setError(null);
    setIsValid(false);
  };
  
  // Verify gift card code
  const verifyGiftCard = async () => {
    // Clean up code for verification
    const cleanCode = giftCardCode.replace(/-/g, '');
    
    if (!cleanCode || cleanCode.length < 8) {
      setError('Please enter a valid gift card code');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      console.log("Verifying gift card code:", formatGiftCardCode(cleanCode));
      
      // Query the database for the gift card
      const giftCardsRef = ref(database, 'giftCards');
      const snapshot = await get(giftCardsRef);
      
      if (!snapshot.exists()) {
        console.log("No gift cards found in database");
        setError('Gift card not found');
        setIsLoading(false);
        return;
      }
      
      // Find the gift card with matching code
      let foundGiftCard = null;
      let giftCardId = null;
      
      snapshot.forEach((childSnapshot) => {
        const card = childSnapshot.val();
        const cardCode = card.code?.replace(/-/g, '').toUpperCase() || '';
        const inputCode = cleanCode.toUpperCase();
        
        if (cardCode === inputCode) {
          foundGiftCard = card;
          giftCardId = childSnapshot.key;
          console.log("Found matching gift card:", giftCardId);
        }
      });
      
      if (!foundGiftCard) {
        console.log("No matching gift card found");
        setError('Gift card not found');
        setIsLoading(false);
        return;
      }
      
      // Ensure balance is a number for proper comparison
      const cardBalance = parseFloat(foundGiftCard.balance);
      if (isNaN(cardBalance)) {
        console.error("Gift card has invalid balance:", foundGiftCard.balance);
        setError('This gift card has an invalid balance');
        setIsLoading(false);
        return;
      }
      
      // Check if gift card is expired
      const expirationDate = new Date(foundGiftCard.expiresAt);
      const now = new Date();
      if (expirationDate < now) {
        console.log("Gift card is expired. Expiration date:", expirationDate);
        setError('This gift card has expired');
        setIsLoading(false);
        return;
      }
      
      // Check if gift card has balance
      if (foundGiftCard.status === 'redeemed' || cardBalance <= 0) {
        console.log("Gift card has no balance. Status:", foundGiftCard.status, "Balance:", cardBalance);
        setError('This gift card has already been redeemed or has no remaining balance');
        setIsLoading(false);
        return;
      }
      
      // Gift card is valid
      setIsValid(true);
      console.log("Gift card is valid. Balance:", cardBalance);
      
      // Call the onApply function with the gift card data
      onApply({
        id: giftCardId,
        code: foundGiftCard.code,
        balance: cardBalance,
        amount: parseFloat(foundGiftCard.amount) || cardBalance,
        expiresAt: foundGiftCard.expiresAt,
        transactions: foundGiftCard.transactions || []
      });
      
      // Clear the input after successful application
      setGiftCardCode('');
      
    } catch (error) {
      console.error('Error verifying gift card:', error);
      setError('An error occurred while verifying the gift card');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Function to refresh card data
  const refreshCardData = async () => {
    if (!appliedGiftCard || !appliedGiftCard.id) return;
    
    setIsLoading(true);
    try {
      console.log("Refreshing gift card data for ID:", appliedGiftCard.id);
      
      // Get the latest gift card data
      const giftCardRef = ref(database, `giftCards/${appliedGiftCard.id}`);
      const snapshot = await get(giftCardRef);
      
      if (snapshot.exists()) {
        const freshCardData = snapshot.val();
        const freshBalance = parseFloat(freshCardData.balance);
        
        console.log("Refreshed gift card data. Current balance:", freshBalance);
        
        // Check if the card is still valid
        const expirationDate = new Date(freshCardData.expiresAt);
        const now = new Date();
        
        if (expirationDate < now) {
          console.log("Gift card is now expired");
          setError('This gift card has expired');
          handleRemove();
          return;
        }
        
        if (freshCardData.status === 'redeemed' || freshBalance <= 0) {
          console.log("Gift card has no remaining balance");
          setError('This gift card has no remaining balance');
          handleRemove();
          return;
        }
        
        // Update the applied gift card with fresh data
        onApply({
          id: appliedGiftCard.id,
          code: freshCardData.code,
          balance: freshBalance,
          amount: parseFloat(freshCardData.amount) || freshBalance,
          expiresAt: freshCardData.expiresAt,
          transactions: freshCardData.transactions || []
        });
        
        console.log("Gift card data refreshed successfully");
      } else {
        console.log("Gift card no longer exists in database");
        setError('Gift card no longer exists');
        handleRemove();
      }
    } catch (error) {
      console.error('Error refreshing gift card data:', error);
      setError('An error occurred while refreshing the gift card data');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle apply button click
  const handleApply = (e) => {
    e.preventDefault();
    verifyGiftCard();
  };
  
  // Handle remove gift card
  const handleRemove = () => {
    setGiftCardCode('');
    setIsValid(false);
    setError(null);
    onRemove();
  };
  
  // Add effect to periodically refresh gift card data when applied
  useEffect(() => {
    let refreshInterval;
    
    if (appliedGiftCard) {
      // Refresh gift card data every 60 seconds to ensure it's still valid
      refreshInterval = setInterval(() => {
        refreshCardData();
      }, 60000); // 60 seconds
    }
    
    return () => {
      if (refreshInterval) clearInterval(refreshInterval);
    };
  }, [appliedGiftCard]);
  
  // If a gift card is already applied, show the applied card UI
  if (appliedGiftCard) {
    return (
      <div className="border border-green-200 bg-green-50 rounded-lg p-4 mb-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <div className="bg-green-100 p-2 rounded-full mr-3">
              <Gift className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <div className="flex items-center">
                <span className="font-medium text-green-800">Gift Card Applied</span>
                <Check className="h-4 w-4 text-green-600 ml-2" />
              </div>
              <div className="text-sm text-green-700">
                <span className="font-mono">{formatGiftCardCode(appliedGiftCard.code)}</span> • 
                Balance: <span className="font-medium">${parseFloat(appliedGiftCard.balance).toFixed(2)}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center">
            <button
              onClick={refreshCardData}
              className="text-blue-500 hover:text-blue-700 mr-3"
              disabled={isLoading}
              aria-label="Refresh gift card data"
              title="Refresh card data"
            >
              <RefreshCw className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            <button 
              onClick={handleRemove}
              className="text-gray-400 hover:text-gray-600"
              aria-label="Remove gift card"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        {error && (
          <div className="mt-2 flex items-center text-sm text-red-600">
            <AlertTriangle className="h-4 w-4 mr-1" />
            <span>{error}</span>
          </div>
        )}
      </div>
    );
  }
  
  return (
    <div className="mb-6">
      <div className="flex items-center mb-3">
        <Gift className="h-5 w-5 text-blue-500 mr-2" />
        <h3 className="text-lg font-medium">Have a Gift Card?</h3>
      </div>
      
      <form onSubmit={handleApply} className="flex flex-col md:flex-row gap-2">
        <div className="flex-grow">
          <input
            type="text"
            value={giftCardCode}
            onChange={handleInputChange}
            placeholder="Enter gift card code (e.g. TEST-1234-ABCD-5678)"
            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
              error 
                ? 'border-red-300 focus:ring-red-200' 
                : isValid 
                  ? 'border-green-300 focus:ring-green-200' 
                  : 'border-gray-300 focus:ring-blue-200'
            }`}
            disabled={isLoading}
          />
          {error && (
            <div className="flex items-center mt-1 text-sm text-red-600">
              <AlertTriangle className="h-4 w-4 mr-1" />
              <span>{error}</span>
            </div>
          )}
        </div>
        
        <button
          type="submit"
          className={`px-6 py-2 rounded-lg font-medium ${
            isLoading 
              ? 'bg-gray-300 text-gray-600 cursor-not-allowed' 
              : 'bg-blue-500 text-white hover:bg-blue-600'
          }`}
          disabled={isLoading}
        >
          {isLoading ? (
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              <span>Verifying...</span>
            </div>
          ) : (
            'Apply'
          )}
        </button>
      </form>
    </div>
  );
};

export default GiftCardRedemption;